import React, { useState, useEffect } from 'react';
import { versionService, VersionComparison as VersionComparisonType } from '../services/versionService';
import { Questionnaire } from '../types';
import VersionComparison from './VersionComparison';

interface VersionHistoryProps {
  questionnaireId: number;
  currentQuestionnaire: Questionnaire;
  onRollback: () => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  questionnaireId,
  currentQuestionnaire,
  onRollback,
}) => {
  const [versions, setVersions] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState<VersionComparisonType | null>(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [questionnaireId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const data = await versionService.getVersionHistory(questionnaireId);
      setVersions(data);
    } catch (error) {
      console.error('Failed to fetch version history:', error);
      alert('获取版本历史失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async (targetVersionId: number) => {
    try {
      setComparing(true);
      const comparisonData = await versionService.compareVersions(
        questionnaireId,
        targetVersionId,
        questionnaireId
      );
      setComparison(comparisonData);
    } catch (error) {
      console.error('Failed to compare versions:', error);
      alert('版本对比失败');
    } finally {
      setComparing(false);
    }
  };

  const handleRollback = async (targetVersionId: number) => {
    if (!window.confirm('确定要回退到此版本吗？当前草稿的所有内容将被替换。')) {
      return;
    }

    try {
      await versionService.rollbackToVersion(questionnaireId, targetVersionId);
      alert('回退成功');
      onRollback();
    } catch (error: any) {
      console.error('Failed to rollback:', error);
      alert(error.response?.data?.message || '回退失败');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-700',
      PUBLISHED: 'bg-success-100 text-success-700',
      ARCHIVED: 'bg-warning-100 text-warning-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const isDraft = currentQuestionnaire.status === 'DRAFT';

  return (
    <>
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium text-blue-900 mb-2">版本管理说明</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• 查看问卷的历史版本</li>
            <li>• 对比不同版本之间的差异</li>
            <li>• 将草稿版本回退到历史版本</li>
          </ul>
          {!isDraft && (
            <div className="mt-2 text-xs text-blue-700">
              注意：只有草稿状态的问卷才能进行回退操作
            </div>
          )}
        </div>

        {versions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-600">暂无版本历史</p>
          </div>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{version.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(version.status)}`}>
                        {version.statusDisplayName}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        {version.version}
                      </span>
                      {version.id === questionnaireId && (
                        <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                          当前版本
                        </span>
                      )}
                    </div>

                    {version.description && (
                      <p className="text-gray-600 text-sm mb-2">{version.description}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>问题数：{version.questionCount}</span>
                      <span>创建者：{version.createdByUsername}</span>
                      <span>创建时间：{new Date(version.createdAt).toLocaleDateString('zh-CN')}</span>
                    </div>
                  </div>

                  {version.id !== questionnaireId && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleCompare(version.id)}
                        disabled={comparing}
                        className="px-3 py-1 text-sm border border-primary-600 text-primary-600 rounded hover:bg-primary-50 disabled:opacity-50"
                      >
                        对比
                      </button>
                      {isDraft && (
                        <button
                          onClick={() => handleRollback(version.id)}
                          className="px-3 py-1 text-sm bg-warning-600 text-white rounded hover:bg-warning-700"
                        >
                          回退
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {comparison && (
        <VersionComparison
          comparison={comparison}
          onClose={() => setComparison(null)}
        />
      )}
    </>
  );
};

export default VersionHistory;
